// Task 206: why an element on the selected breakpoint is hidden / conditionally shown. Pure; node-tested.
// Relative (not %/) so node --test can import this pure module.
// eslint-disable-next-line no-restricted-imports
import {
  VISIBILITY_NEVER,
  VISIBILITY_WEB_ONLY,
  VISIBILITY_MOBILE_ONLY,
  VISIBILITY_CONDITIONAL,
  isVisibilityExpression,
  normalizeVisibility,
} from "./visibility-surface.js";
// eslint-disable-next-line no-restricted-imports
import { applyPreviewOverride } from "./visibility-preview.js";

const TONE_HIDDEN = "hidden";
const TONE_CONDITIONAL = "conditional";

function sourceText({ inherited, fromName, breakpointName }) {
  if (!inherited) {
    return `set on ${breakpointName}`;
  }
  return fromName ? `inherited from ${fromName}` : "inherited from a broader breakpoint";
}

function liveValuesText(liveValues) {
  const pairs = Object.entries(liveValues ?? {}).map(([name, value]) => `${name} = ${JSON.stringify(value)}`);
  return pairs.join(", ");
}

function explainExpression(input) {
  const { expression, visibleNow } = input;
  const text = expression?.text || "(condition)";
  const live = liveValuesText(expression?.liveValues);
  const detail = live ? `${text} — now ${live}` : text;
  if (expression?.value === undefined) {
    const missing = expression?.missing ?? [];
    const why = missing.length
      ? `missing context ${missing.map(name => `"${name}"`).join(", ")}`
      : "a value it reads is unavailable";
    return {
      kind: "expression-unresolvable",
      tone: TONE_HIDDEN,
      title: "Hidden: condition can't be evaluated here",
      body: `${text} can't be evaluated (${why}), so it fails closed`,
      detail,
    };
  }
  if (visibleNow) {
    return { kind: "expression-true", tone: TONE_CONDITIONAL, title: "Shown conditionally", body: `${text} is true right now`, detail };
  }
  return { kind: "expression-false", tone: TONE_HIDDEN, title: "Hidden by a condition", body: `shown only when ${text}`, detail };
}

function explainStatic(input) {
  const { raw, awaitingEvent } = input;
  if (raw === VISIBILITY_CONDITIONAL) {
    return {
      kind: "conditional-unset",
      tone: TONE_CONDITIONAL,
      title: "Conditional",
      body: "no condition set yet, so it shows",
    };
  }
  const value = normalizeVisibility(raw);
  if (value === VISIBILITY_NEVER && awaitingEvent) {
    return {
      kind: "awaiting-event",
      tone: TONE_HIDDEN,
      title: `Hidden until "${awaitingEvent}" fires`,
      body: `popup: Visible is Never (${sourceText(input)}) until the event reveals it`,
    };
  }
  if (value === VISIBILITY_NEVER) {
    return {
      kind: "never",
      tone: TONE_HIDDEN,
      title: "Hidden",
      body: input.inherited
        ? `Visible is Never, ${sourceText(input)}`
        : `Visible is set to Never on ${input.breakpointName}`,
    };
  }
  if (value === VISIBILITY_MOBILE_ONLY) {
    return { kind: "mobile-only", tone: TONE_HIDDEN, title: "Mobile app only", body: `hidden on the web (${sourceText(input)})` };
  }
  if (value === VISIBILITY_WEB_ONLY) {
    return { kind: "web-only", tone: TONE_CONDITIONAL, title: "Web only", body: `hidden in the mobile app (${sourceText(input)})` };
  }
  return null;
}

// input: { raw, visibleNow, breakpointName, inherited, fromName, expression?, awaitingEvent?, rows? }.
// null = plain "visible always": no callout.
export function explainVisibility(input) {
  const explanation = isVisibilityExpression(input.raw) ? explainExpression(input) : explainStatic(input);
  if (!explanation) {
    return null;
  }
  const rows = input.rows;
  const title = rows && rows.total > 1 ? `${explanation.title} (hidden in ${rows.hidden} of ${rows.total} rows)` : explanation.title;
  return {
    ...explanation,
    title,
    detail: explanation.detail ?? explanation.body,
    visibleNow: Boolean(input.visibleNow),
  };
}

export function expressionContextNames(ast) {
  const names = [];
  const walk = node => {
    if (!node || typeof node !== "object") {
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if ((node.source === "context" || node.source === "contextField") && node.context !== undefined) {
      const name = String(node.context);
      if (!names.includes(name)) {
        names.push(name);
      }
      return;
    }
    Object.values(node).forEach(walk);
  };
  walk(ast);
  return names;
}

// A hidden element's descendants vanish with it; calling them out too is noise.
export function pruneDescendantsOfHidden(entries) {
  const hidden = new Set(entries.filter(entry => !entry.visibleNow).map(entry => String(entry.id)));
  return entries.filter(entry => !(entry.ancestorIds ?? []).some(id => hidden.has(String(id))));
}

// One template element rendered per feed row → one callout, anchored on the first hidden row.
export function aggregateCalloutInstances(instances) {
  const ordered = [...instances].sort((a, b) => a.order - b.order);
  const hiddenRows = ordered.filter(instance => !instance.visibleNow);
  return {
    representative: hiddenRows[0] ?? ordered[0],
    rows: ordered.length > 1 ? { hidden: hiddenRows.length, total: ordered.length } : null,
  };
}

// registry: { [elementId]: { [instanceKey]: { order, baseVisible, ancestorIds, input } } } for the selected breakpoint.
export function collectFrameCallouts(registry, overrides) {
  const entries = [];
  for (const [id, instances] of Object.entries(registry ?? {})) {
    const keyed = Object.entries(instances).map(([instanceKey, instance]) => ({ ...instance, instanceKey, visibleNow: instance.baseVisible }));
    if (keyed.length === 0) {
      continue;
    }
    const { representative, rows } = aggregateCalloutInstances(keyed);
    const explanation = explainVisibility({ ...representative.input, visibleNow: representative.baseVisible, rows });
    if (!explanation) {
      continue;
    }
    const override = overrides?.[id] ?? null;
    entries.push({
      id: String(id),
      instanceKey: representative.instanceKey,
      order: representative.order,
      ancestorIds: representative.ancestorIds ?? [],
      explanation,
      override,
      baseVisible: representative.baseVisible,
      displayed: applyPreviewOverride(override, representative.baseVisible),
    });
  }
  const pruned = pruneDescendantsOfHidden(entries.map(entry => ({ ...entry, visibleNow: entry.displayed })));
  return pruned.sort((a, b) => a.order - b.order);
}
